import React from 'react';
import { graphql } from 'gatsby';
import styled, { ThemeProvider } from 'styled-components';
import { Card as SCCard } from '@keytrade/components-card';

import Title from '@/components/Title';
import Paragraph from '@/components/Paragraph';
import { mediaQuery } from '@/utils/helpers';
import RichText, { RichTextType } from './Richtext';
import Link, { LinkStyle, LinkType } from './Link';
import About, { AboutType } from './About';
import Image, { ImageType } from '@/components/Image';

const TransparentCardWrapper = styled.li`
  padding: 0 1.6rem;

  ${mediaQuery.md(`
        padding: 0 2.4rem;
    `)}
`;
const CardWrapper = styled.li`
  display: flex;
`;
const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  word-break: break-word;
`;

const CardDescription = styled.div`
  flex: 1;
  margin: 20px 0 0;
  ${mediaQuery.lg(`
    margin: 16px 0 0;
  `)}
`;

const CTAWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  margin-top: 24px;
`;

const Card = styled(SCCard)`
  width: 100%;
  overflow: hidden;
  isolation: isolate;
`;
const CardImage = styled(Image)<TransparentProps>`
  ${({ transparent }) =>
    transparent && 'border-radius: 2.4rem; isolation: isolate;'}
`;
type TransparentProps = {
  transparent: boolean;
};
const CardInfo = styled.div<TransparentProps>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: ${({ transparent }) =>
    transparent
      ? `2.4rem 0 0 0;
  ${mediaQuery.md('3.2rem 0 0 0;')}`
      : `2.4rem;
  ${mediaQuery.md('3.2rem;')}
  `};
`;

export type Props = LargeCardData;

export type LargeCardData = RichTextType & {
  about: AboutType;
  title: string;
  parentDark: boolean;
  subtitle: string;
  primaryLink: LinkType;
  secondaryLink: LinkType;
  image: ImageType;
  cardBackground: any;
};

const LargeCard: React.FC<Props> = (props) => {
  const background: string = props.cardBackground?.cardBackground;
  const transparent: boolean = background === 'No frame';
  const dark: boolean =
    background === 'Dark' || (transparent && props.parentDark);
  const options = { theme: dark ? 'dark' : 'white' };
  const paragraphColor: string = dark ? 'White' : 'GreyBlue';
  const renderCardContent = (
    props: LargeCardData,
    dark: boolean,
    transparent: boolean,
  ) => (
    <ThemeProvider theme={{ dark: dark }}>
      <CardContent>
        {props.image && (
          <CardImage transparent={transparent} {...props.image} />
        )}
        <CardInfo transparent={transparent}>
          {props.about && <About data={props.about} dark={dark} />}
          <Title size='lg' level='h3' margin='0' lightColor={dark}>
            {props.title}
          </Title>
          {props.subtitle && (
            <Paragraph margin={'0 0 22px 0'} color={paragraphColor}>
              {props.subtitle}
            </Paragraph>
          )}

          {props.richText && (
            <CardDescription>
              <RichText
                data={props.richText}
                defaultPaths={props.defaultPaths}
                options={options}
                isInternalLinkButton
              />
            </CardDescription>
          )}
          {(props.primaryLink || props.secondaryLink) && (
            <CTAWrapper>
              <Link {...props.primaryLink} linkStyle={LinkStyle.PRIMARY} />
              <Link {...props.secondaryLink} linkStyle={LinkStyle.SECONDARY} />
            </CTAWrapper>
          )}
        </CardInfo>
      </CardContent>
    </ThemeProvider>
  );

  return transparent ? (
    <TransparentCardWrapper>
      {renderCardContent(props, dark, transparent)}
    </TransparentCardWrapper>
  ) : (
    <CardWrapper>
      <Card large={true} dark={dark} fillContainer={true}>
        {renderCardContent(props, dark, transparent)}
      </Card>
    </CardWrapper>
  );
};

export default LargeCard;

export const largeCardFragmentQuery = graphql`
  fragment LargeCardFragment on ContentfulLargeCard {
    __typename
    about {
      ...AboutFragment
    }
    image {
      gatsbyImageData(width: 1024)
      ...ImageFragment
    }
    title
    subtitle
    cardBackground {
      cardBackground
    }
    richText {
      ...RichTextFragment
    }
    primaryLink {
      ...LinkFragment
    }
    secondaryLink {
      ...LinkFragment
    }
  }
`;
